import { useCallback, useEffect, useState } from 'react'
import { UseFormHandleSubmit, UseFormReset } from 'react-hook-form'
import { toast } from 'react-toastify'
import { toastSyncSuccess } from 'src/utils/syncToast'
import { useAuth } from 'src/contexts/auth'
import { DeathsAndDiseasesDto, UpdateDeathsAndDiseasesDto } from 'src/dto/deaths-and-diseases.dto'
import { useUpdateDeathsAndDiseases } from 'src/hooks/api/deaths-and-diseases'
import { useUpdateAnimal, useCreateAnimalHistoric } from 'src/hooks/api/animal'

import { AnimalController } from 'src/controllers/animal.controller'
import { useQueryClient } from '@tanstack/react-query'
import { Animal } from 'src/domain/animal/Animal'
import { AnimalHistoric, AnimalHistoricParams } from 'src/domain/animal/AnimalHistoric'
import { IAnimal } from 'src/dto/animal.dto'

interface UseRegisterProps {
  data: DeathsAndDiseasesDto | null
  handleSubmit: UseFormHandleSubmit<UpdateDeathsAndDiseasesDto>
  reset: UseFormReset<UpdateDeathsAndDiseasesDto>
  onClose: () => void
  onOpen: () => void
}

export const useRegister = ({ handleSubmit, onClose, data, reset }: UseRegisterProps) => {
  const [multipleRegister, setMultipleRegister] = useState<boolean>(false)
  const { userFarm: farmId } = useAuth()

  const { mutateAsync: updateDeathsAndDiseases, isPending: isLoadingUpdateDeathsAndDiseases } =
    useUpdateDeathsAndDiseases()

  const { mutateAsync: updateAnimalMutation } = useUpdateAnimal()
  const { mutateAsync: createAnimalHistoricMutation } = useCreateAnimalHistoric()

  const queryClient = useQueryClient()

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
    }
  }, [])

  const onSubmit = handleSubmit(async (formData: DeathsAndDiseasesDto) => {
    try {
      const animalData = await queryClient.fetchQuery({
        queryKey: ['getAnimalById', farmId, formData.idAnimal],
        queryFn: () => new AnimalController().getAnimalById(farmId!, formData.idAnimal!)
      })

      await updateDeathsAndDiseases({
        idFazenda: farmId!,
        ...formData
      })

      if (animalData) {
        const now = new Date().toISOString()

        const animalUpdateData = new Animal({
          ...animalData,
          dataMorte: new Date(formData.data).toISOString(),
          categoria: 'MT',
          dataAtual: now,
          tela: 'morte',
          acao: 'Atualizacao'
        })

        await updateAnimalMutation(animalUpdateData)

        toastSyncSuccess(`Item ${data?.id ? 'atualizado' : 'salvo'} com sucesso!`)

        if (multipleRegister) {
          reset()
        } else {
          onClose()
        }

        if (data?.id) return

        const historicData = new AnimalHistoric({
          idFazenda: farmId!,
          idAnimal: formData.idAnimal!,
          idFazendaIdAnimal: `${farmId}#${formData.idAnimal!}`,
          idHistorico: `${farmId}#${formData.idAnimal!}`,
          data: now,
          peso: animalData.pesoAtual || 0,
          perimetro: animalData.perimetro || 0,
          fitaComprimento: animalData.fitaComprimento || 0,
          fitaPerimetro: animalData.fitaPerimetro || 0,
          categoria: 'MT',
          idPasto: animalData.idPastoAtual || '',
          descricao: animalData.nome || '',
          sexo: animalData.sexo,
          morte: true,
          nascimento: false,
          compra: false,
          venda: false,
          animalComprado: animalData.animalComprado || false,
          tela: 'detalhesAnimal',
          ganhoDePeso: 0,
          meses: 0,
          acao: 'Atualizacao'
        })

        await createAnimalHistoricMutation(historicData as AnimalHistoricParams)
      }

      if (multipleRegister) {
        reset()
      } else {
        onClose()
      }

      queryClient.invalidateQueries({ queryKey: ['getDeathsAndDiseasesList', farmId] })
    } catch (err: any) {
      console.error(err)
      toast.error('Erro ao salvar os dados, tente novamente.')
    }
  })

  const animaisFilter = (animalList: IAnimal[]) => {
    return animalList.filter((animal) => animal.categoria !== 'MT' && animal.categoria !== 'VD')
  }

  useEffect(() => {
    if (data) reset(data)
  }, [data, reset])

  return {
    isLoading: isLoadingUpdateDeathsAndDiseases,
    multipleRegister,
    onSubmit,
    handleKeyDown,
    setMultipleRegister,
    animaisFilter
  }
}
